import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Checkbox, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../hooks/useNotifications'

// ---------------------------------------------------------------------------
// Screenshot capture
// ---------------------------------------------------------------------------

async function captureScreenshot(containerRef) {
  if (containerRef.current) {
    containerRef.current.style.visibility = 'hidden'
  }

  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    })

    return await new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.srcObject = stream
      video.onloadedmetadata = () => {
        video.play()
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        stream.getTracks().forEach((t) => t.stop())
        video.srcObject = null
        try {
          resolve(canvas.toDataURL('image/png'))
        } catch (err) {
          reject(err)
        }
      }
      video.onerror = reject
    })
  } finally {
    if (containerRef.current) {
      containerRef.current.style.visibility = ''
    }
  }
}

// ---------------------------------------------------------------------------
// Report assembly
// ---------------------------------------------------------------------------

/**
 * Everything else the report needs — network requests, console output, navigation and errors —
 * is recorded automatically by the Sentry SDK as breadcrumbs on the captured event, so the modal
 * only assembles the page context and the optional screenshot.
 */
function assembleReport({ screenshotDataUrl }) {
  return {
    timestamp: new Date().toISOString(),
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screenshotDataUrl: screenshotDataUrl || undefined,
  }
}

// ---------------------------------------------------------------------------
// PayloadPreview
// ---------------------------------------------------------------------------

function PayloadPreview({ includeScreenshot }) {
  const report = useMemo(
    () => assembleReport({ screenshotDataUrl: includeScreenshot ? '[screenshot data]' : undefined }),
    [includeScreenshot]
  )

  return (
    <pre
      data-testid="payload-preview"
      style={{
        maxHeight: 200,
        overflowY: 'auto',
        fontSize: 11,
        background: '#f5f5f5',
        padding: 8,
        margin: 0,
      }}
    >
      {JSON.stringify(report, null, 2)}
    </pre>
  )
}

// ---------------------------------------------------------------------------
// ReportModal
// ---------------------------------------------------------------------------

export default function ReportModal({ onClose, onSubmit }) {
  const { t } = useTranslation()
  const { ok, error: notifyError } = useNotifications()
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotError, setScreenshotError] = useState(null)
  const [submitState, setSubmitState] = useState('idle')
  const containerRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleOutsideClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.()
    },
    [onClose]
  )

  const handleScreenshot = useCallback(async () => {
    setScreenshotError(null)
    try {
      const dataUrl = await captureScreenshot(containerRef)
      setScreenshot(dataUrl)
      setIncludeScreenshot(true)
    } catch (err) {
      if (err?.name !== 'NotAllowedError') {
        setScreenshotError(t('report.screenshotFailed', 'Screenshot failed. Please try again.'))
      }
    }
  }, [t])

  const handleSubmit = useCallback(async () => {
    setSubmitState('loading')

    let capturedScreenshot = screenshot
    if (includeScreenshot && !capturedScreenshot) {
      try {
        capturedScreenshot = await captureScreenshot(containerRef)
        setScreenshot(capturedScreenshot)
      } catch {
        // Capture failed or was cancelled — submit without screenshot
        capturedScreenshot = null
      }
    }

    try {
      const report = assembleReport({ screenshotDataUrl: includeScreenshot ? capturedScreenshot : null })
      await onSubmit?.(report)
      setSubmitState('success')
      ok(t('report.submitSuccess', 'Report submitted successfully.'))
      onClose?.()
    } catch (err) {
      setSubmitState('idle')
      notifyError(err?.message || t('report.submitFailed', 'Report submission failed.'), err)
    }
  }, [includeScreenshot, notifyError, ok, onClose, onSubmit, screenshot, t])

  const isSubmitting = submitState === 'loading'
  const isSuccess = submitState === 'success'

  const modalContent = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      data-testid="report-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={handleOutsideClick}
    >
      <dialog
        ref={containerRef}
        open
        aria-label={t('report.title', 'Submit a Report')}
        style={{
          background: '#fff',
          borderRadius: 8,
          width: 600,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 24,
          border: 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: '1px solid #e5e5e5',
          }}
        >
          <h2 style={{ margin: 0, flexGrow: 1, fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px' }}>
            {t('report.title', 'Submit a Report')}
          </h2>
          <Button emphasis="low" onClick={onClose} aria-label={t('action.close', 'Close')}>
            ✕
          </Button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Checkbox
            name="screenshot"
            label={t('report.screenshot', 'Screenshot')}
            checked={includeScreenshot}
            onChange={() => setIncludeScreenshot((v) => !v)}
          />
          {!screenshot ? (
            <Button size="sm" emphasis="medium" onClick={handleScreenshot}>
              {t('report.captureNow', 'Capture now')}
            </Button>
          ) : (
            <span style={{ color: 'green', fontSize: 13 }}>{t('report.captured', 'Captured ✓')}</span>
          )}
        </div>
        {screenshotError && <p style={{ color: 'red', fontSize: 12, margin: '4px 0 0' }}>{screenshotError}</p>}

        <Separator size="sm" />

        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '4px 0' }}>
            {t('report.jsonPayloadPreview', 'JSON payload preview')}
          </summary>
          <div style={{ marginTop: 8 }}>
            <PayloadPreview includeScreenshot={includeScreenshot} />
          </div>
        </details>

        <Separator size="sm" />

        <div style={{ display: 'flex', gap: 12 }}>
          <Button emphasis="medium" onClick={onClose}>
            {t('action.cancel', 'Cancel')}
          </Button>
          <Button emphasis="high" onClick={handleSubmit} disabled={isSubmitting || isSuccess}>
            {isSubmitting ? t('report.submitting', 'Submitting…') : t('action.submit', 'Submit')}
          </Button>
        </div>
      </dialog>
    </div>
  )

  return createPortal(modalContent, document.body)
}
